import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { ChevronDown, ArrowRight } from 'lucide-react';

const ARCHETYPES = [
  {
    key: 'C', name: 'The Cultivator', tagline: 'She who tends', color: '#7C6A52',
    essence: "The Cultivator's intelligence lives in the body, in the hands, in the sacred return. She is in the dirt every day \u2014 not because she has to be, but because she understands something the other archetypes are still learning: that nothing flourishes without tending. Her devotion is not maintenance \u2014 it is ministry.",
    gift: "She carries the intelligence of deep time. She understands that mastery is not a destination but a practice you inhabit, day after day. Her gift is the ability to sustain devotion without external validation. What she builds lasts because she builds slowly, deliberately, and with love made visible.",
    shadow: "Her deepest wound is self-dismissal. She has internalized the message that slow, methodical work is less valuable than speed and spectacle. Her shadow is the belief that devotion without drama doesn't count.",
    mythology: ['Earth \u00b7 Soil \u00b7 Root', 'Demeter (Greek)', 'Prithvi / Bhumi (Hindu)', 'The Green Tara (Tibetan)'],
    business: ['Operational excellence', 'Systems & processes', 'Sustainable execution', 'Client retention', 'Team culture & care'],
    invitation: "Your devotion is the foundation everything else stands on. The invitation is to claim it as power \u2014 not support.",
    reflection: "Where have you dismissed your own consistency as 'just how I am' rather than claiming it as a form of genius?"
  },
  {
    key: 'W', name: 'The Weaver', tagline: 'She who connects', color: '#6B7A5C',
    essence: "The Weaver sees the relationship between things. She holds the whole system in her peripheral vision while managing every moving thread. Her intelligence is relational and structural simultaneously, which makes her rare and often invisible. She is the connective tissue of every endeavor.",
    gift: "She sees the whole and the parts at the same time. She tracks how details talk to each other, how a decision in one corner ripples downstream. Her gift is relational perception \u2014 a kind of somatic knowing that the system is off before the metrics show it.",
    shadow: "She can become the invisible glue \u2014 the one holding everything together while no one holds her. She may stay in weaving mode long past the point where she needs to release.",
    mythology: ['Water \u00b7 Thread \u00b7 Web', 'Ariadne (Greek)', 'Spider Grandmother (Hopi)', 'Saraswati as pattern-holder (Hindu)'],
    business: ['Project coherence', 'Cross-functional leadership', 'Quality assurance', 'Strategic memory', 'Team integration'],
    invitation: "You see what others miss and hold what others drop. The invitation is to name your gift explicitly \u2014 to call it systems intelligence.",
    reflection: "What are you holding together right now that might need to be released?"
  },
  {
    key: 'P', name: 'The Priestess', tagline: 'She who interprets', color: '#7B6E9E',
    essence: "The Priestess holds the threshold between the earthly and the cosmic. She is the interpreter \u2014 the one who takes the Dreamer's vision and makes it legible, livable, and real. She is the axis point of the framework: anchored enough to be trusted, expansive enough to receive.",
    gift: "She holds the capacity to be fully present to both the invisible and the visible at the same time, and to translate between them without losing the integrity of either. Her discernment is a form of love \u2014 precise, unflinching, offered in service of the whole.",
    shadow: "Her shadow is over-functioning dressed as discernment. She can become the bottleneck of the very system she's holding together. Her wound is the fear that if she steps back, the whole thing collapses.",
    mythology: ['Fire \u00b7 Threshold \u00b7 Altar', 'The Pythia at Delphi (Greek)', 'Isis as sacred interpreter (Egyptian)', 'Hecate at the crossroads (Greek)'],
    business: ['Strategic discernment', 'Brand clarity & integrity', 'Culture-keeping', 'Leadership vision', 'Community guidance'],
    invitation: "You are not the bridge for everyone. The invitation is to trust that the system you've built has intelligence of its own.",
    reflection: "What would you have to trust to stop being the only one who holds the whole picture?"
  },
  {
    key: 'S', name: 'The Seeker', tagline: 'She who discovers', color: '#5E7EA8',
    essence: "The Seeker moves toward what is not yet known. She is drawn by questions, activated by problems, energized by the hunt for a better way. She is the bridge between the known and the emergent. Her intelligence is curious and rigorous at once.",
    gift: "Her gift is a particular kind of courage: the willingness to not know, and to stay in that not-knowing long enough to find something true. She doesn't need to be right. She needs to know what's true. That distinction is everything.",
    shadow: "She can become addicted to the search itself, perpetually in research mode and never in integration. Completion can feel like loss \u2014 because the hunt is where she feels most alive.",
    mythology: ['Air \u00b7 Lightning \u00b7 Horizon', 'Artemis as the huntress (Greek)', 'Saraswati as wisdom-seeker (Hindu)', 'Inanna\'s descent (Sumerian)'],
    business: ['Innovation & R&D', 'Market intelligence', 'Product development', 'Problem-solving', 'Strategic research'],
    invitation: "The discovery is not complete until it lands somewhere. The invitation is to let yourself arrive \u2014 to stop searching and start building.",
    reflection: "What truth have you already found that you're still researching?"
  },
  {
    key: 'D', name: 'The Dreamer', tagline: 'She who imagines', color: '#9B8EC4',
    essence: "The Dreamer exists at the edge of what is possible. She does not build from what is \u2014 she builds from what could be. Her intelligence is visionary, non-linear, and cosmically sourced. She is the beginning of everything the other archetypes make real.",
    gift: "She carries the capacity to receive what hasn't arrived yet and hold it with enough conviction that others begin to believe in it too. She speaks the thing into existence before it's safe to say. She gives the entire system its reason for being.",
    shadow: "She can live so far upstream that she loses connection to embodiment. She may use visioning as avoidance \u2014 staying in the exhilaration of possibility rather than sitting with the slower ache of manifestation.",
    mythology: ['Ether \u00b7 Stars \u00b7 Void', 'Nyx, goddess of night (Greek)', 'Nut, the sky goddess (Egyptian)', 'Shakti as pure creative fire (Hindu)'],
    business: ['Vision casting', 'Brand origination', 'Creative direction', 'Culture shaping', 'New venture ideation'],
    invitation: "Your visions need a body to travel through. The invitation is to stay long enough to let what you see become something others can touch.",
    reflection: "Which vision have you abandoned just as it was asking you to get your hands dirty?"
  }
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

export default function SacredFeminineFrameworkPage() {
  usePageTitle('Sacred Feminine Thinking Archetypes');
  const [openCard, setOpenCard] = useState(null);

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Header */}
      <section className="pt-16 sm:pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            <motion.p variants={fadeUp} className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.2em] text-[hsl(var(--silver))] mb-6">
              Framework
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))] mb-4">
              The Sacred Feminine<br />Thinking Archetypes
            </motion.h1>
            <motion.p variants={fadeUp} className="font-['Inter'] text-sm text-[hsl(var(--silver))]">
              Earth &middot; Embodiment &middot; Synthesis &middot; Vision &middot; Cosmos
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Spectrum bar */}
      <section className="pb-12">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 rounded-full border border-[rgba(168,181,178,0.12)] px-5 py-3">
            <span className="font-['Inter'] text-[9px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] shrink-0">Earthly</span>
            <div className="flex-1 flex items-center justify-between">
              {ARCHETYPES.map(a => (
                <div
                  key={a.key}
                  className="w-4 h-4 rounded-full transition-transform duration-300 hover:scale-150 cursor-pointer"
                  style={{ background: a.color }}
                  title={a.name}
                  onClick={() => setOpenCard(openCard === a.key ? null : a.key)}
                />
              ))}
            </div>
            <span className="font-['Inter'] text-[9px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] shrink-0">Cosmic</span>
          </div>
        </div>
      </section>

      {/* Quiz CTA */}
      <section className="pb-8">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <Link to="/sacred-feminine-quiz">
            <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-8 py-5 rounded-lg hover:brightness-[1.03]">
              Take the Quiz to Discover Your Archetype
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Archetype Cards */}
      <section className="py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-4">
          {ARCHETYPES.map((a, i) => {
            const isOpen = openCard === a.key;
            return (
              <motion.div
                key={a.key}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
                variants={fadeUp}
              >
                <div className="rounded-xl border border-[rgba(168,181,178,0.12)] overflow-hidden bg-[hsl(var(--card))] hover:border-[rgba(201,168,76,0.2)] transition-colors">
                  {/* Header */}
                  <button
                    onClick={() => setOpenCard(isOpen ? null : a.key)}
                    className="w-full flex items-center gap-4 p-5 sm:p-6 text-left"
                    data-testid={`archetype-card-${a.key}`}
                  >
                    <div className="w-1 h-10 rounded-full shrink-0" style={{ background: a.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-['Cormorant_Garamond'] text-xl font-bold text-[hsl(var(--foreground))]">{a.name}</p>
                      <p className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))]">{a.tagline}</p>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-[hsl(var(--silver))] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Body */}
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                      className="px-5 sm:px-6 pb-6 border-t border-[rgba(168,181,178,0.08)] pt-6"
                    >
                      {/* Essence */}
                      <div className="mb-6">
                        <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-3">Essence</p>
                        <p className="font-['Cormorant_Garamond'] text-base sm:text-lg italic leading-[1.6] text-[hsl(var(--foreground))]">{a.essence}</p>
                      </div>

                      {/* Gift + Shadow */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-3">Core Gift</p>
                          <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))]">{a.gift}</p>
                        </div>
                        <div>
                          <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--rose))] mb-3">Shadow Side</p>
                          <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))]">{a.shadow}</p>
                        </div>
                      </div>

                      {/* Mythology */}
                      <div className="mb-6">
                        <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-3">Mythological Anchors</p>
                        <div className="flex flex-wrap gap-2">
                          {a.mythology.map((m, j) => (
                            <span key={j} className="font-['Inter'] text-xs px-3 py-1.5 rounded-full border border-[rgba(168,181,178,0.15)] text-[hsl(var(--muted-foreground))]">{m}</span>
                          ))}
                        </div>
                      </div>

                      {/* Business */}
                      <div className="mb-6">
                        <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-3">In Business She Is...</p>
                        <div className="flex flex-wrap gap-2">
                          {a.business.map((b, j) => (
                            <span key={j} className="font-['Inter'] text-xs px-3 py-1.5 rounded-full border border-[rgba(201,168,76,0.15)] text-[hsl(var(--champagne))]">{b}</span>
                          ))}
                        </div>
                      </div>

                      {/* Invitation */}
                      <div className="rounded-lg border-l-2 border-[hsl(var(--gold))] pl-5 py-3 mb-6 bg-[rgba(201,168,76,0.04)]">
                        <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-2">Her Invitation</p>
                        <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--foreground))]">{a.invitation}</p>
                      </div>

                      {/* Reflection */}
                      <div className="rounded-lg border-l-2 border-[hsl(var(--rose))] pl-5 py-3 bg-[rgba(185,105,122,0.04)]">
                        <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--rose))] mb-2">Ponder This</p>
                        <p className="font-['Cormorant_Garamond'] text-base italic leading-[1.5] text-[hsl(var(--champagne))]">{a.reflection}</p>
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 sm:py-24 section-mist">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--foreground))] mb-4">
            Which archetype leads your thinking?
          </h2>
          <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-8">
            Take the 5-minute quiz to discover your primary and secondary archetypes.
          </p>
          <Link to="/sacred-feminine-quiz">
            <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-10 py-6 rounded-lg hover:brightness-[1.03]">
              Take the Quiz
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
